import React, { useEffect, useState } from 'react';
import Modal from '@mui/material/Modal';
import Checkbox from '@mui/material/Checkbox';
import PropTypes from 'prop-types';
import CloseIcon from '@mui/icons-material/Close';
import {
  Button, Grid, Typography, TextField, MenuItem,
} from '@material-ui/core';
import clsx from 'clsx';
import projectRequest from '../../../requests/api/project';
import { formatDayMonthAndYearMoment } from '../../../utils/date';
import useStyles from './styles';

const getUniqueDevelopers = (projectDetails) => {
  if (!projectDetails?.project_cells) return [];
  const devMap = new Map();
  projectDetails.project_cells.forEach((cell) => {
    (cell.project_developers || []).forEach((pd) => {
      const dev = pd.assignation?.developer;
      if (!dev) return;
      const devId = dev.id;
      if (!devMap.has(devId)) {
        devMap.set(devId, {
          id: devId,
          name: dev.user?.name || '',
          lastname: dev.user?.lastname || '',
          roles: new Set(),
        });
      }
      (pd.project_roles || []).forEach((role) => {
        devMap.get(devId).roles.add(role.name);
      });
    });
  });
  return Array.from(devMap.values()).map((d) => ({
    ...d,
    roles: Array.from(d.roles),
  }));
};

const CloseProjectModal = ({
  open, setOpen, handleCloseProject, project,
}) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [projectDetails, setProjectDetails] = useState(null);
  const [serviceType, setServiceType] = useState('');
  const [selectedDevIds, setSelectedDevIds] = useState(() => new Set());

  useEffect(() => {
    let cancelled = false;
    const loadDetails = async () => {
      if (!open || !project?.id) return;
      setLoading(true);
      setProjectDetails(null);
      setServiceType(project.serviceType || '');
      try {
        const res = await projectRequest.getProyectById(project.id);
        if (cancelled) return;
        if (res.success) {
          const loaded = res.data?.data?.project || null;
          setProjectDetails(loaded);
          if (loaded?.serviceType) {
            setServiceType(loaded.serviceType);
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadDetails();
    return () => { cancelled = true; };
  }, [open, project?.id]);

  const handleClose = () => {
    setOpen(false);
  };

  const developers = getUniqueDevelopers(projectDetails);

  useEffect(() => {
    setSelectedDevIds(new Set(developers.map((d) => d.id)));
  }, [projectDetails]);

  const toggleDev = (devId) => {
    setSelectedDevIds((prev) => {
      const next = new Set(prev);
      if (next.has(devId)) next.delete(devId);
      else next.add(devId);
      return next;
    });
  };
  const kickOff = projectDetails?.kickOffDate
    ? formatDayMonthAndYearMoment(projectDetails.kickOffDate)
    : '—';

  return (
    <Modal open={open} onClose={handleClose} className={classes.modalContainer}>
      <div>
        <div className={classes.modalContent}>
          <div className={classes.modalHeaderContainer}>
            <div className={classes.modalTitleContainer}>
              <Typography className={classes.modalTitle}>
                ¿Estas seguro que quieres cerrar este proyecto?
              </Typography>
            </div>
            <CloseIcon
              className={classes.closeIcon}
              onClick={handleClose}
              sx={{ fontSize: 40 }}
            />
          </div>
          <hr className={classes.modalLine} />
          <div className={classes.modalBodyContainer}>
            <div className={classes.textContainer}>
              <Typography
                className={clsx(classes.typography, classes.typography)}
              >
                ¿Quieres cerrar el proyecto?
              </Typography>
              <Typography
                className={clsx(classes.typography, classes.boldTypography)}
              >
                {`${project.client.name} - ${project.name}`}
              </Typography>
            </div>

            <div className={classes.infoSection}>
              <Typography className={classes.infoSectionTitle}>
                Fecha de inicio
              </Typography>
              {loading ? (
                <Typography className={classes.loadingText}>
                  Cargando...
                </Typography>
              ) : (
                <Typography className={classes.infoRow}>
                  {`${kickOff}`}
                </Typography>
              )}
            </div>

            <div className={classes.infoSection}>
              <Typography className={classes.infoSectionTitle}>
                Equipo involucrado
              </Typography>
              {loading && (
                <Typography className={classes.loadingText}>
                  Cargando...
                </Typography>
              )}
              {!loading && developers.length === 0 && (
                <Typography className={classes.loadingText}>
                  Sin equipo asignado
                </Typography>
              )}
              {!loading && developers.length > 0 && (
                <ul className={classes.developerList}>
                  {developers.map((dev) => {
                    const fullName = `${dev.name} ${dev.lastname}`.trim();
                    const checked = selectedDevIds.has(dev.id);
                    return (
                      <li key={dev.id} className={classes.developerItem}>
                        <Checkbox
                          checked={checked}
                          onChange={() => toggleDev(dev.id)}
                          size="small"
                          sx={{ padding: '0 8px 0 0' }}
                        />
                        <span className={classes.developerName}>
                          {fullName || '(Sin nombre)'}
                        </span>
                        {dev.roles.length > 0 && (
                          <>
                            <span className={classes.developerSeparator}>{' — '}</span>
                            <span className={classes.developerRole}>
                              {dev.roles.join(' · ')}
                            </span>
                          </>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className={classes.infoSection}>
              <Typography className={classes.infoSectionTitle}>
                Tipo de servicio *
              </Typography>
              <TextField
                select
                value={serviceType}
                onChange={(e) => setServiceType(e.target.value)}
                variant="outlined"
                fullWidth
                size="small"
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value="">
                  <em>Selecciona un tipo...</em>
                </MenuItem>
                <MenuItem value="Proyecto">Proyecto</MenuItem>
                <MenuItem value="Soporte">Soporte</MenuItem>
                <MenuItem value="Celula Agil">Célula Ágil</MenuItem>
                <MenuItem value="Diseño">Diseño</MenuItem>
                <MenuItem value="Otro">Otro</MenuItem>
              </TextField>
            </div>

            <Typography
              className={clsx(classes.typography)}
              style={{ marginTop: '20px' }}
            >
              Recuerda revisar bien antes de confirmar el cierre
            </Typography>
          </div>
        </div>
        <div className={classes.buttonsContainer}>
          <Grid container spacing={2} justifyContent="center">
            <Grid item>
              <Button
                variant="contained"
                color="secondary"
                className={classes.modalButton}
                onClick={handleClose}
              >
                Cancelar
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                color="secondary"
                className={classes.modalButton}
                onClick={() => handleCloseProject(serviceType, Array.from(selectedDevIds))}
                disabled={!serviceType}
              >
                Cerrar Proyecto
              </Button>
            </Grid>
          </Grid>
        </div>
      </div>
    </Modal>
  );
};

CloseProjectModal.propTypes = {
  className: PropTypes.string,
  open: PropTypes.bool,
  setOpen: PropTypes.func.isRequired,
  handleCloseProject: PropTypes.func.isRequired,
  project: PropTypes.object.isRequired,
};

export default CloseProjectModal;
